/**
 * Analyzer: Day Time-of-Day
 *
 * Two observations this analyzer can emit:
 *   1. Session split — morning/midday/afternoon winner + loser when clearly asymmetric
 *   2. Best hour — a specific hour bucket that carried the day (≥2 trades in that hour)
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';
import { humanHour } from '../../dayUtils';

export const key = 'dayTimeOfDay';
export const tier = 'free';

export const analyze = (ctx) => {
  const { day } = ctx;
  if (!day || !day.time) return null;

  const out = [];
  const { buckets, bestHour } = day.time;

  // Session-period split — only when we have ≥2 periods with trades
  const periods = [
    { name: 'Morning',   data: buckets.morning },
    { name: 'Midday',    data: buckets.midday  },
    { name: 'Afternoon', data: buckets.afternoon }
  ].filter(p => p.data.trades > 0);

  if (periods.length >= 2) {
    const sorted = [...periods].sort((a, b) => a.data.pnl - b.data.pnl);
    const worst = sorted[0];
    const best  = sorted[sorted.length - 1];
    if (best.data.pnl > 0 && worst.data.pnl < 0) {
      out.push(makeObservation({
        id: `${key}:${day.date}:period`,
        analyzer: key,
        scope: 'day',
        scopeId: day.date,
        severity: SEVERITY.INFO,
        category: CATEGORY.EXECUTION,
        title: `${best.name} was your sweet spot`,
        detail: `${best.name}: +$${best.data.pnl.toFixed(0)} (${best.data.winRate.toFixed(0)}% WR). ${worst.name}: −$${Math.abs(worst.data.pnl).toFixed(0)}.`,
        action: `Consider tighter criteria — or stepping back — during ${worst.name.toLowerCase()} until the pattern flips.`,
        evidence: { buckets, periods: sorted },
        metric: { label: 'Best Window', value: best.name, unit: '' },
        confidence: 0.75,
        priority: 58
      }));
    }
  }

  // Best hour — meaningful when ≥2 trades inside that hour
  if (bestHour && bestHour.trades >= 2 && bestHour.pnl > 0) {
    out.push(makeObservation({
      id: `${key}:${day.date}:besthour`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.EXECUTION,
      title: `${humanHour(bestHour.hour)} was your best hour`,
      detail: `+$${bestHour.pnl.toFixed(0)} from ${bestHour.trades} trades in the ${humanHour(bestHour.hour)} block.`,
      action: `Start watching for this ${humanHour(bestHour.hour)} window in your pre-market prep — it's a recurring edge.`,
      evidence: { bestHour },
      metric: { label: 'Hour P&L', value: `+$${bestHour.pnl.toFixed(0)}`, unit: '' },
      confidence: 0.7,
      priority: 40
    }));
  }

  return out.length > 0 ? out : null;
};
